//Shared Reference Data
import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import type { RawAxiosRequestHeaders } from "axios";
import { AxiosInstance } from "axios";
import { createApiClient } from "./useApiClient";
import { useSafeRequest } from "./useSafeRequest";
import type { AppLocation, InputLocation } from "../models/AppLocation";
import { useUser } from '../components/UserContext';
import { useIsAuthenticated } from "@azure/msal-react";
import type { AppCategory } from "../models/Category";
import type { ArtworkStatusDto} from "../models/ArtworkStatus";
import type { NoteTypeDto } from "../models/Note";

export type Artist = {
    id: number;
    name: string;
}

export type Currency = {
    currencyCode: string;
    currencyName: string;
}

export type Source = {
    id: number;
    type: string;
}

type DatasetState<T> = {
    data: T[] | null;
    loading: boolean;
    error?: string;
};

interface ReferenceDataContextType {
    artists: Artist[] | null;
    categories: AppCategory[] | null;
    currencies: Currency[] | null;
    sources: Source[] | null;
    locations: InputLocation[] | null;
    statuses: ArtworkStatusDto[] | null;
    noteTypes: NoteTypeDto[] | null;

    loadingAny: boolean;
    errorAny?: string;
    isReady: boolean; // true when all are loaded successfully

    // actions
    refresh: () => Promise<void>;

    // granular status
    artistsState: DatasetState<Artist>;
    categoriesState: DatasetState<AppCategory>;
    currenciesState: DatasetState<Currency>;
    sourcesState: DatasetState<Source>;
    locationsState: DatasetState<InputLocation>;
    statusesState: DatasetState<ArtworkStatusDto>;
    noteTypesState: DatasetState<NoteTypeDto>;

    //Locations
    loadingLocations: boolean;
    errorLocations?: string;
    refreshLocations: () => Promise<void>;
    invalidateLocations: () => void;
}



const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const ReferenceDataContext = createContext<ReferenceDataContextType | undefined>(undefined);

type Props = {
    children: React.ReactNode;
    client?: AxiosInstance;
};

export const ReferenceDataProvider: React.FC<Props> = ({ children, client }) => {
    const MAX_ATTEMPTS = 5;

    const backoffDelay = (attempt: number, base = 400, cap = 12000) => {
        // exponential: base * 2^(attempt-1), with jitter 0.5x–1.5x, capped
        const exp = Math.min(cap, base * Math.pow(2, Math.max(0, attempt - 1)));
        const jitter = 0.5 + Math.random(); // 0.5–1.5
        return Math.round(exp * jitter);
    };

    type RetryTimers = Record<string, number | null>;

    // keep timers so you don't queue duplicates and to allow cleanup
    const retryTimersRef = React.useRef<RetryTimers>({});

    // cancel any pending timer by key
    const cancelRetry = (key: string) => {
        const id = retryTimersRef.current[key];
        if (id != null) {
            clearTimeout(id);
            retryTimersRef.current[key] = null;
        }
    };

    const queueRetry = (
        key: string,
        startAttempt: number,
        maxAttempts: number,
        fn: () => Promise<void>
    ) => {
        cancelRetry(key); // ensure only one timer per key
        if (startAttempt > maxAttempts) return;

        const delay = backoffDelay(startAttempt);
        const id = window.setTimeout(async () => {
            try {
                await fn();                     // if this succeeds, we’re done
                retryTimersRef.current[key] = null;
            } catch {
                // schedule next attempt
                queueRetry(key, startAttempt + 1, maxAttempts, fn);
            }
        }, delay);

        retryTimersRef.current[key] = id;
    };
    const [reload, setReload] = useState<number>(0);
    const isAuthenticated = useIsAuthenticated();
    const { activeClientId, getAccessToken } = useUser();
    const didRunRef = React.useRef(false);

    const clientIdRef = React.useRef<number | null>(null);
    React.useEffect(() => { clientIdRef.current = activeClientId; }, [activeClientId]);

    const api = React.useMemo(
        () => createApiClient({ getAccessToken, getClientId: () => clientIdRef.current }, { timeoutMs: 12000 }),
        [getAccessToken]
    );

    // independent request hooks
    const artistsReq = useSafeRequest<any[]>(api);
    const categoriesReq = useSafeRequest<any[]>(api);
    const currenciesReq = useSafeRequest<any[]>(api);
    const sourcesReq = useSafeRequest<any[]>(api);
    const locationsReq = useSafeRequest<any[]>(api);
    const statusesReq = useSafeRequest<any[]>(api);
    const noteTypesReq = useSafeRequest<any[]>(api);


    // local state variables that hold *resolved* reference data
    const [artists, setArtists] = React.useState<any[] | null>(null);
    const [categories, setCategories] = React.useState<any[] | null>(null);
    const [currencies, setCurrencies] = React.useState<any[] | null>(null);
    const [sources, setSources] = React.useState<any[] | null>(null);
    const [locations, setLocations] = React.useState<any[] | null>(null);
    const [statuses, setStatuses] = React.useState<any[] | null>(null);
    const [noteTypes, setNoteTypes] = React.useState<any[] | null>(null);

    const loadAll = React.useCallback(async () => {

        const locationsP = locationsReq.run({ method: "GET", url: "locations" });

        await sleep(60); // small stagger to avoid burst on cold start

        const tasks = [
            artistsReq.run({ method: "GET", url: "artists" }),
            categoriesReq.run({ method: "GET", url: "categories" }),
            currenciesReq.run({ method: "GET", url: "currencies" }),
            sourcesReq.run({ method: "GET", url: "sources" }),
            statusesReq.run({ method: "GET", url: "artwork-statuses" }),
            noteTypesReq.run({ method: "GET", url: "admin/note-types" }),
            locationsP,
        ];

        const [a, c, cur, s, sta, n, l] = await Promise.allSettled(tasks);


        if (a.status === "fulfilled") setArtists(a.value);
        else queueRetry("artists", 1, MAX_ATTEMPTS, async () => {
            const data = await artistsReq.run({ method: "GET", url: "artists" });
            setArtists(data);
        });

        if (c.status === "fulfilled") setCategories(c.value);
        else queueRetry("categories", 1, MAX_ATTEMPTS, async () => {
            const data = await categoriesReq.run({ method: "GET", url: "categories" });
            setCategories(data);
        });

        if (cur.status === "fulfilled") setCurrencies(cur.value);
        else queueRetry("currencies", 1, MAX_ATTEMPTS, async () => {
            const data = await currenciesReq.run({ method: "GET", url: "currencies" });
            setCurrencies(data);
        });

        if (s.status === "fulfilled") setSources(s.value);
        else queueRetry("sources", 1, MAX_ATTEMPTS, async () => {
            const data = await sourcesReq.run({ method: "GET", url: "sources" });
            setSources(data);
        });

        if (sta.status === "fulfilled") setStatuses(sta.value);
        else queueRetry("statuses", 1, MAX_ATTEMPTS, async () => {
            const data = await statusesReq.run({ method: "GET", url: "artwork-statuses" });
            setStatuses(data);
        });

        if (n.status === "fulfilled") setNoteTypes(n.value);
        else queueRetry("noteTypes", 1, MAX_ATTEMPTS, async () => {
            const data = await noteTypesReq.run({ method: "GET", url: "admin/note-types" });
            setNoteTypes(data);
        });

        if (l.status === "fulfilled") setLocations(l.value);
        else queueRetry("locations", 1, MAX_ATTEMPTS, async () => {
            const data = await locationsReq.run({ method: "GET", url: "locations" });
            setLocations(data);
        });

    }, [artistsReq, categoriesReq, currenciesReq, sourcesReq, statusesReq, noteTypesReq, locationsReq]);

    React.useEffect(() => {
        // Gate: only load when authenticated and client chosen
        if (!isAuthenticated || activeClientId == null) return;

        // Guard StrictMode double-invoke in dev
        if (didRunRef.current) return;
        didRunRef.current = true;

        loadAll().catch(() => { });
    }, [isAuthenticated, activeClientId, loadAll]);

    // Reload **whenever activeClientId actually changes**
    const lastClientId = React.useRef<number | null>(null);
    // optional: coalesce rapid changes
    const debounce = React.useRef<number | null>(null);


    const refresh = React.useCallback(() => {
        return loadAll();
    }, [loadAll]);

    React.useEffect(() => {
        return () => {
            Object.values(retryTimersRef.current).forEach((id) => id != null && clearTimeout(id));
        };
    }, []);

    //Specifically (re)load Locations
    const refreshLocations = async () => {
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) return;

            const apiHeader: RawAxiosRequestHeaders = {
                Authorization: `Bearer ${accessToken}`,
                'X-Client-Id': activeClientId?.toString() || ''
            };

            locationsReq.run({ method: "GET", url: "locations", headers: apiHeader }).then(d => setLocations(d)).catch(() => { })
        } catch {
            /* error available on req.error */
        }
    };


    // --- Invalidation plumbing (debounced + cross-tab) ---
    const debounceRef = useRef<number | null>(null);
    const bcRef = useRef<BroadcastChannel | null>(null);

    React.useEffect(() => {
        if (!isAuthenticated || activeClientId == null) return;

        if (lastClientId.current === activeClientId) return; // no-op if same
        lastClientId.current = activeClientId;

        // debounce to avoid burst if UI toggles clients quickly
        if (debounce.current) window.clearTimeout(debounce.current);
        debounce.current = window.setTimeout(() => {
            loadAll().catch(() => { });
            debounce.current = null;
        }, 120);
    }, [isAuthenticated, activeClientId, loadAll]);

    useEffect(() => {
        // Cross-tab listen
        bcRef.current = "BroadcastChannel" in window ? new BroadcastChannel("locations") : null;
        const bc = bcRef.current;
        const onMsg = (e: MessageEvent) => {
            if (e.data?.type === "locations:changed") {
                scheduleRefresh();
            }
        };
        bc?.addEventListener("message", onMsg);

        // Same-tab custom event listen
        const onCustom = () => scheduleRefresh();
        window.addEventListener("locations:changed", onCustom);

        return () => {
            bc?.removeEventListener("message", onMsg);
            bc?.close();
            window.removeEventListener("locations:changed", onCustom);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const scheduleRefresh = () => {
        if (debounceRef.current) window.clearTimeout(debounceRef.current);
        debounceRef.current = window.setTimeout(() => {
            refreshLocations();
            debounceRef.current = null;
        }, 150); // coalesce bursts of edits
    };

    const invalidateLocations = () => {
        // same-tab
        window.dispatchEvent(new Event("locations:changed"));
        // cross-tab
        bcRef.current?.postMessage({ type: "locations:changed" });
        // and refresh here too (in case listeners aren’t attached yet)
        scheduleRefresh();
    };

    // Derived flags
    const loadingAny = artistsReq.loading || categoriesReq.loading || currenciesReq.loading || sourcesReq.loading || locationsReq.loading || statusesReq.loading || noteTypesReq.loading;

    // Show a single top-level error if any dataset failed on the *latest* attempt
    const errorAny =
        artistsReq.error || categoriesReq.error || currenciesReq.error || sourcesReq.error || locationsReq.error || statusesReq.error || undefined;

    const isReady = !!(artists && categories && currencies && sources && locations && statuses);

    const value: ReferenceDataContextType = {
        artists,
        categories,
        currencies,
        sources,
        locations,
        statuses,
        noteTypes,
        loadingAny,
        errorAny,
        isReady,
        refresh,
        artistsState: { data: artists, loading: artistsReq.loading, error: artistsReq.error },
        categoriesState: { data: categories, loading: categoriesReq.loading, error: categoriesReq.error },
        currenciesState: { data: currencies, loading: currenciesReq.loading, error: currenciesReq.error },
        sourcesState: { data: sources, loading: sourcesReq.loading, error: sourcesReq.error },
        locationsState: { data: locations, loading: locationsReq.loading, error: locationsReq.error },
        statusesState: { data: statuses, loading: statusesReq.loading, error: statusesReq.error },
        noteTypesState: { data: noteTypes, loading: noteTypesReq.loading, error: noteTypesReq.error },
        loadingLocations: locationsReq.loading,
        errorLocations: locationsReq.error,
        refreshLocations,
        invalidateLocations,
    };

    return (
        <ReferenceDataContext.Provider value={value}>
            {children}
        </ReferenceDataContext.Provider>
    );
};

export const useReferenceData = (): ReferenceDataContextType => {
    const context = useContext(ReferenceDataContext);
    if (!context) {
        throw new Error('useReferenceData must be used within a ReferenceDataProvider');
    }
    return context;
};
